import { FaceTecURScanMatchInitializationError, FaceTecURScanMatchSessionStatus } from "../../../core-sdk/FaceTecURScanMatchSDK.js/FaceTecURScanMatchPublicAPI";
import { DeveloperStatusMessages } from "./DeveloperStatusMessages";
import { SampleAppUIFunctions } from "./SampleAppUIFunctions";
import { URScanMatchStatusEnumFriendlyText } from "./URScanMatchStatusEnumFriendlyText";
import { Config } from "../Config";

export class URCodesSampleUtilities {
  private static isSafariBrowser: boolean = navigator.vendor.indexOf("Apple") === 0 &&
    navigator.userAgent.toLowerCase().indexOf("chrom") === -1 &&
    navigator.userAgent.toLowerCase().indexOf("crios") === -1;
  private static isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  private static isMobileDevice: boolean = SampleAppUIFunctions.isLikelyMobileDevice();

  public static demonstrateHandlingInitializationSuccess = (): void => {
    new SampleAppUIFunctions("#status-and-loading-overlay-container").fadeOut(600, () => {
      this.fadeInMainUI();

      new SampleAppUIFunctions("#button-container").fadeIn(300);
    });
  };

  public static demonstrateHandlingInitializationError = (faceTecInitializationError: FaceTecURScanMatchInitializationError): void => {
    this.disableAllButtons();
    new SampleAppUIFunctions("#status-and-loading-overlay-container").fadeOut(600, () => {
      this.fadeInMainUI();

      new SampleAppUIFunctions("#status").fadeIn(300);

      const sdk = (window as any).FaceTecURScanMatchSDK;
      if (faceTecInitializationError === sdk.FaceTecURScanMatchInitializationError.DeviceNotSupported) {
        if (this.isIOS && !this.isSafariBrowser) {
          DeveloperStatusMessages.logAndDisplayMessage("System Not Compatible: " + sdk.FaceTecURScanMatchInitializationError[faceTecInitializationError] + " Please ensure your iOS version is 14.3 or greater.");
        }
        else {
          DeveloperStatusMessages.logAndDisplayMessage("System Not Compatible: " + sdk.FaceTecURScanMatchInitializationError[faceTecInitializationError]);
        }
      }
      else {
        DeveloperStatusMessages.logAndDisplayMessage("Initialization Unsuccessful: " + sdk.FaceTecURScanMatchInitializationError[faceTecInitializationError]);
      }
    });
  };

  public static demonstrateHandlingSessionComplete = (sessionStatus: FaceTecURScanMatchSessionStatus): void => {
    this.enableAllButtons();
    new SampleAppUIFunctions("#status").fadeIn(1);

    const sdk = (window as any).FaceTecURScanMatchSDK;
    if (sessionStatus === sdk.FaceTecURScanMatchSessionStatus.SessionCompleted) {
      // Display message to user, Success messages are already logged from the processors.
      DeveloperStatusMessages.displayMessage("See logs for details");
    }
    else {
      // Log status for unSuccess Sessions.
      DeveloperStatusMessages.logSessionStatusOnSessionComplete(sessionStatus);
      DeveloperStatusMessages.displayMessage(URScanMatchStatusEnumFriendlyText.descriptionForSessionStatus(sessionStatus));
    }

    this.fadeInMainUI();
  };

  public static fadeInMainUI = (): void => {
    if (document.getElementById("status-and-loading-overlay-container") != null) {
      new SampleAppUIFunctions("#status-and-loading-overlay-container").fadeOut(600, function() {
        document.getElementById("status-and-loading-overlay-container")?.remove();
      });
    }

    new SampleAppUIFunctions("#wrapping-box-container").fadeIn(300);
    new SampleAppUIFunctions("footer").fadeIn(300);
  };

  public static fadeOutMainUIAndPrepareForSession = (): void => {
    this.disableAllButtons();
    new SampleAppUIFunctions("footer").fadeOut(1000);
    new SampleAppUIFunctions("#status").hide();

    new SampleAppUIFunctions("#wrapping-box-container").fadeOut(1500);
  };

  public static disableAllButtons = (): void => {
    document.querySelectorAll("button").forEach((button) => {
      (<HTMLButtonElement>button).disabled = true;
    });
  };

  public static enableAllButtons = (): void => {
    document.querySelectorAll("button").forEach((button) => {
      (<HTMLButtonElement>button).disabled = false;
    });
  };

  public static setupBrowserInterfaceBasedOnDevice = (): void => {
    console.log("[FaceTec Debug] setupBrowserInterfaceBasedOnDevice called - Build v2.7");

    // Check if SDK is available on window
    const sdk = typeof window !== 'undefined' && (window as any).FaceTecURScanMatchSDK;
    console.log("[FaceTec Debug] window.FaceTecURScanMatchSDK exists:", !!sdk);
    console.log("[FaceTec Debug] SDK has version function:", sdk && typeof sdk.version === 'function');

    if (sdk && typeof sdk.version === 'function') {
      try {
        const version = sdk.version().replace("-SNAPSHOT", "");
        console.log("[FaceTec Debug] SDK version retrieved:", version);
        document.getElementById("app-version")!.innerHTML = Config.BuildVersion + " (SDK " + version + ")";
        console.log("[FaceTec Debug] Version set in DOM:", Config.BuildVersion + " (SDK " + version + ")");
      } catch (e) {
        console.error("[FaceTec Debug] Error getting SDK version:", e);
        document.getElementById("app-version")!.innerHTML = Config.BuildVersion;
        console.log("[FaceTec Debug] Set default version due to error");
      }
    } else {
      console.warn("[FaceTec Debug] SDK not available or version function not found, setting default version");
      document.getElementById("app-version")!.innerHTML = Config.BuildVersion;
    }

    console.log("[FaceTec Debug] setupBrowserInterfaceBasedOnDevice completed");

    if (this.isMobileDevice) {
      var windowHeight = window.innerHeight;
      var windowWidth = window.innerWidth;

      // Make all the footer font sizes the same, set width to 90% and adjust the left margin
      var footerFontSize = "100%";

      if (windowWidth < 768) {
        footerFontSize = "9px";
      }

      if (windowWidth < 411) {
        footerFontSize = "8px";
      }

      if (windowWidth < 375) {
        footerFontSize = "7px";
      }

      new SampleAppUIFunctions("footer").css({
        "font-size": footerFontSize,
        width: "90%",
        margin: "0",
        "line-height": "9px"
      });

      new SampleAppUIFunctions("footer span p").css({ "font-size": "inherit" });

      // Remove margins on footer elements
      new SampleAppUIFunctions("footer span, footer span p").css({ margin: 0 });

      // Get the length of the hidden copy of the copyright string. The longest string in the footer
      var computedFooterFontSize = window.getComputedStyle(document.querySelector("footer span p")!).fontSize;
      new SampleAppUIFunctions("#copy-right-length").css({ "font-size": computedFooterFontSize });
      var copyRightStringLength = document.getElementById("copy-right-length")!.clientWidth;
      new SampleAppUIFunctions("hr").css({ width: copyRightStringLength + "px" });

      if (windowWidth! <= 500) {
        new SampleAppUIFunctions("#certi-images img").css({ height: "30px" });
      }

      new SampleAppUIFunctions("hr").css({ width: copyRightStringLength + "px" });
      new SampleAppUIFunctions("html").css({ position: "fixed", height: windowHeight + "px", width: "100%" });
      new SampleAppUIFunctions("body").css({ height: "inherit" });
      new SampleAppUIFunctions(".big-button").css({ width: "80%", height: "60px", "font-size": "medium" });

      this.styleMobileDeviceV2();
    }
    else {
      document.querySelector("footer")!.style.opacity = "0";
      document.querySelector("footer")!.classList.remove("display-none");
    }
  };

  public static styleMobileDeviceV2 = (): void => {
    const innerBodyElement = document.getElementById("inner-body")!;
    const wrappingBoxContainerElement = document.getElementById("wrapping-box-container")!;
    const controlsContainerElement = document.getElementById("controls")!;
    const buttonContainerElement = document.getElementById("button-container")!;
    const footerElement = document.querySelector("footer")!;
    const statusElement = document.getElementById("status")!;
    const logoContainer = document.getElementById("facetec-logo-container")!;
    const logoImageElement = document.querySelector(".main-logo")! as HTMLElement;
    const statusContainerElement = document.createElement("div");

    document.getElementById("app-version")!.style.display = "none";

    document.querySelectorAll(".metadata").forEach((element: Element) => {
      (element as HTMLElement).style.padding = "0";
    });

    if (window.innerHeight <= 450) {
      document.querySelectorAll(".section-header").forEach((element: Element) => {
        (element as HTMLElement).style.fontSize = "small";
      });
    }

    document.querySelectorAll("#security-data div").forEach((element: Element, index: number) => {
      // Dont change the header
      if (index !== 0) {
        (element as HTMLElement).style.lineHeight = "20px";
      }
    });

    innerBodyElement.style.display = "flex";
    innerBodyElement.style.flexFlow = "column";
    innerBodyElement.style.justifyContent = "space-between";
    innerBodyElement.style.height = "100%";

    wrappingBoxContainerElement.style.display = "flex";
    wrappingBoxContainerElement.style.flexFlow = "column";
    wrappingBoxContainerElement.style.flexGrow = "1";
    wrappingBoxContainerElement.style.justifyContent = "center";
    wrappingBoxContainerElement.style.alignItems = "center";
    wrappingBoxContainerElement.style.width = "100%";
    wrappingBoxContainerElement.style.height = "100%";
    wrappingBoxContainerElement.style.margin = "0";

    controlsContainerElement.style.height = "100%";
    controlsContainerElement.style.minHeight = "240px";
    controlsContainerElement.style.padding = "0";
    controlsContainerElement.style.border = "0px";
    controlsContainerElement.style.justifyContent = "center";
    controlsContainerElement.style.rowGap = "20px";

    buttonContainerElement.style.height = "auto";

    new SampleAppUIFunctions(".browser-button").css({ "max-width": "350px"});


    footerElement.style.display = "flex";
    footerElement.style.flexFlow = "column";
    footerElement.style.margin = "5px auto";
    footerElement.style.position = "unset";
    footerElement.style.bottom = "unset";
    footerElement.style.left = "unset";
    footerElement.style.transform = "unset";
    footerElement.style.opacity = "0";
    footerElement.classList.remove("display-none");

    statusElement.style.height = "100%";
    statusElement.style.padding = "0";
    statusElement.style.fontSize = "8px";

    logoContainer.style.display = "flex";
    logoContainer.style.flexFlow = "column";
    logoContainer.style.maxHeight = "95px";
    logoContainer.style.minHeight = "40px";

    logoImageElement.style.display = "flex";
    logoImageElement.style.width = "auto";
    logoImageElement.style.minHeight = "30px";
    logoImageElement.style.maxHeight = "unset";
    logoImageElement.style.height = "unset";
    logoImageElement.style.margin = "0 auto";

    statusElement.remove();
    statusContainerElement.appendChild(statusElement);
    buttonContainerElement.appendChild(statusContainerElement);
    controlsContainerElement.style.height = (Math.floor(document.querySelector("body")!.getBoundingClientRect().height) - Math.floor(document.querySelector("footer")!.getBoundingClientRect().height) - 12).toString() + "px";
  };
}